/**
 * Orchestrator hook for the dictation flow.
 *
 * Coordinates recording → transcript capture → SSE processing → result handling.
 * Sends the transcript + field schema as a `question` to the existing ask-gemini
 * SSE endpoint. Accumulates streamed tokens, parses the JSON answer, and calls
 * onResult with the extracted field values.
 * @module features/voice-report/hooks/useDictation
 */

import { useCallback, useMemo, useRef } from "react";
import { Platform } from "react-native";
import EventSource, { type CustomEvent } from "react-native-sse";

import { useAudioRecorder } from "./useAudioRecorder";
import {
  dictationFilledFieldsVar,
  dictationProcessingMessageVar,
  dictationResultVar,
  dictationStatusVar,
  dictationTranscriptVar,
  resetDictationState,
} from "../stores/dictationStore";

import { resolveInputLocale } from "@/constants/locales";
import { useAuth, useCurrentUser } from "@/features/auth";
import { usePlayerDetailSupportedLocalesQuery } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { env } from "@/lib/env";
import { getFreshAccessToken } from "@/providers/accessTokenSource";
import {
  DEFAULT_LOCALE,
  getDeviceLocale,
  getLocaleDisplayName,
  localeToBCP47,
} from "@/utils/locale";

/** SSE event types emitted by the ask-gemini endpoint */
type SSEEvents = "start" | "status" | "token" | "end";

/**
 * Builds the question prompt that instructs the LLM to extract form field
 * values from a scout's spoken transcript and return structured JSON.
 * @param transcript - The scout's spoken words
 * @param fieldSchema - Form field definitions (key → type/label/values)
 * @param transcriptLanguage - Display name of the transcript language (e.g. "English", "Spanish")
 * @returns Formatted question string for the ask-gemini endpoint
 */
const buildExtractionQuestion = (
  transcript: string,
  fieldSchema: Record<string, unknown>,
  transcriptLanguage: string = "English"
): string =>
  `Extract structured data from this football scout's verbal report and return ONLY a valid JSON object.
The transcript below is spoken in ${transcriptLanguage}.

## Form Fields (use these exact keys)
${JSON.stringify(fieldSchema, null, 2)}

## Scout's Transcript
"${transcript}"

## Rules
1. For "select"/"selectboxes" fields, use ONLY the allowed values listed.
2. For "textarea" fields, write a clean summary of what the scout said about that topic.
3. For "number" fields, extract the numeric value.
4. Only include fields where the scout clearly provided relevant information.
5. Return ONLY valid JSON — no explanation, no markdown fences, no extra text.

## Response Format
{"fieldKey1": "extracted value", "fieldKey2": "another value"}`;

/**
 * Props for useDictation hook
 */
interface UseDictationProps {
  /** Form field schema from Form.io definition */
  readonly fieldSchema: Record<string, unknown> | null;
  /** Match context (teams, players, date) */
  readonly matchContext?: Record<string, unknown>;
  /** Player ID for this report */
  readonly playerId: string;
  /** Report type ID */
  readonly reportTypeId: string;
  /** Called when dictation result is ready for WebView injection */
  readonly onResult: (
    data: Record<string, unknown>,
    filledFields: readonly string[]
  ) => void;
}

/**
 * Return type for useDictation hook
 */
interface UseDictationReturn {
  /** Start recording */
  readonly startRecording: () => Promise<void>;
  /** Finish recording and start processing */
  readonly finishRecording: () => Promise<void>;
  /** Cancel recording */
  readonly cancelRecording: () => Promise<void>;
  /** Reset all state */
  readonly reset: () => void;
  /** Whether recording is active */
  readonly isRecording: boolean;
  /** Elapsed recording time */
  readonly elapsedSeconds: number;
  /** Live transcript from speech recognition */
  readonly liveTranscript: string;
  /** Whether microphone permission is granted */
  readonly hasPermission: boolean;
  /** Request microphone permission */
  readonly requestPermission: () => Promise<boolean>;
  /** Real-time frequency levels from Web Audio API (0-1 range) */
  readonly audioLevels: readonly number[];
}

/**
 * Parses the accumulated LLM response as JSON, stripping markdown fences.
 * Returns null on parse failure.
 * @param text - Raw accumulated token text
 * @returns Parsed JSON object or null if parsing fails
 */
const parseExtractedFields = (text: string): Record<string, unknown> | null => {
  const cleaned = text
    .replace(/^```json?\s*/i, "")
    .replace(/```\s*$/i, "")
    .trim();
  try {
    return JSON.parse(cleaned) as Record<string, unknown>;
  } catch {
    console.error("[useDictation] Failed to parse LLM response as JSON:", {
      raw: text.substring(0, 500),
    });
    return null;
  }
};

/**
 * Orchestrator for the full dictation flow.
 * @param props - Hook configuration
 * @param props.fieldSchema - Form field definitions
 * @param props.matchContext - Match context data
 * @param props.playerId - Player ID
 * @param props.reportTypeId - Report type ID
 * @param props.onResult - Callback with structured result
 * @returns Dictation controls
 */
export const useDictation = ({
  fieldSchema,
  playerId,
  onResult,
}: UseDictationProps): UseDictationReturn => {
  // 1. VARIABLES & STATE
  const auth = useAuth();
  const currentUser = useCurrentUser();
  const toast = useToastNotification();
  const eventSourceRef = useRef<EventSource | null>(null);
  const onResultRef = useRef(onResult);
  const accumulatedTokensRef = useRef("");
  const transcriptRef = useRef("");

  const { data: localesData, loading: localesLoading } =
    usePlayerDetailSupportedLocalesQuery();
  const supportedLocales = useMemo(
    () => localesData?.supportedLocales ?? [DEFAULT_LOCALE],
    [localesData?.supportedLocales]
  );

  /*
   * SE-6509. The Settings row is now the app's one **Language** control, and
   * it labels a stranded preference through `resolveDisplayLocale` — `ja_JP`
   * reads as "English". Handing the RAW `ja_JP` to speech recognition here
   * would make that row false: it would say English while the microphone
   * listened in Japanese, with no offered option the user could pick to fix
   * it. `resolveInputLocale` is the same rule the label uses, so the two can
   * no longer disagree. The stored profile value is untouched — this is
   * resolution, not migration (see the note in `@/constants/locales`).
   *
   * The device-locale fallback SE-4235 added survives: it is passed in as the
   * no-preference case rather than removed, and only narrowed to the six.
   */
  const resolvedLocale = useMemo(
    () =>
      resolveInputLocale(
        currentUser?.languagePreference,
        localesLoading ? DEFAULT_LOCALE : getDeviceLocale(supportedLocales)
      ),
    [currentUser?.languagePreference, localesLoading, supportedLocales]
  );

  const dictationLang = useMemo(
    () => localeToBCP47(resolvedLocale),
    [resolvedLocale]
  );

  const {
    startRecording,
    stopRecording,
    cancelRecording: cancelAudioRecording,
    hasPermission,
    requestPermission,
    isRecording,
    elapsedSeconds,
    liveTranscript,
    audioLevels,
  } = useAudioRecorder(dictationLang);

  const sendToSSE = useCallback(
    (transcript: string, accessToken: string | undefined) => {
      dictationStatusVar("processing");
      dictationProcessingMessageVar(
        "Analyzing your scout report and extracting form fields..."
      );
      accumulatedTokensRef.current = "";
      transcriptRef.current = transcript;

      const sseEndpoint = env.EXPO_PUBLIC_SSE_ENDPOINT || "";
      const userId = currentUser?.id || "";
      const tenantId =
        currentUser?.organizationId || env.EXPO_PUBLIC_DEFAULT_TENANT_ID || "";

      const resolvedFieldSchema = fieldSchema ?? {};
      const languageLabel = getLocaleDisplayName(
        resolvedLocale,
        supportedLocales
      );
      const question = buildExtractionQuestion(
        transcript,
        resolvedFieldSchema,
        languageLabel
      );

      // `completion`: the extraction prompt is a full instruction that must
      // come back as a plain model completion — never through Ask Gemini v2's
      // intent classifier and agent loop (SE-8432).
      const request = {
        question,
        sessionId: `${tenantId}#${userId}#dictation-${Date.now()}`,
        accessKey: accessToken || undefined,
        mode: "completion",
      };

      console.info("[useDictation] SSE request:", {
        endpoint: sseEndpoint,
        transcriptLength: transcript.length,
        transcriptPreview: transcript.substring(0, 200),
        fieldSchemaKeys: Object.keys(resolvedFieldSchema),
        questionLength: question.length,
        playerId,
        hasAccessToken: Boolean(accessToken),
      });

      const headers = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      };

      const es = new EventSource<SSEEvents>(sseEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(request),
        pollingInterval: 0,
      });

      eventSourceRef.current = es;

      es.addEventListener("open", () => {
        console.info("[useDictation] SSE connection opened");
      });

      es.addEventListener("start", () => {
        console.info("[useDictation] SSE processing started");
      });

      es.addEventListener("status", (event: CustomEvent<"status">) => {
        if (!event.data) return;
        try {
          const parsed = JSON.parse(event.data) as { message?: string };
          console.info("[useDictation] SSE status:", parsed);
          // Ignore backend status messages — show dictation-specific text instead
          dictationProcessingMessageVar(
            "Analyzing your scout report and extracting form fields..."
          );
        } catch {
          console.warn("[useDictation] Unparseable SSE status:", event.data);
        }
      });

      es.addEventListener("token", (event: CustomEvent<"token">) => {
        if (!event.data) return;
        try {
          const parsed = JSON.parse(event.data) as { content?: string };
          if (parsed.content) {
            accumulatedTokensRef.current += parsed.content;
          }
        } catch {
          // Raw token text
          accumulatedTokensRef.current += event.data;
        }
      });

      es.addEventListener("end", () => {
        console.info("[useDictation] SSE end event, processing result");
        const accumulated = accumulatedTokensRef.current.trim();

        console.info("[useDictation] Accumulated LLM response:", {
          length: accumulated.length,
          preview: accumulated.substring(0, 500),
        });

        const extracted = parseExtractedFields(accumulated);

        if (extracted && Object.keys(extracted).length > 0) {
          const fieldsExtracted = Object.keys(extracted);

          console.info("[useDictation] Fields extracted:", {
            fieldsExtracted,
            data: extracted,
          });

          dictationResultVar(extracted);
          dictationTranscriptVar(transcriptRef.current);
          dictationFilledFieldsVar(fieldsExtracted);
          dictationStatusVar("completed");
          dictationProcessingMessageVar("");

          onResultRef.current(extracted, fieldsExtracted);
        } else {
          console.warn("[useDictation] No fields extracted from response");
          dictationStatusVar("error");
          toast.show({
            action: "danger",
            description:
              "Could not extract fields from your dictation. Please try again with more detail.",
          });
        }
      });

      es.addEventListener("message", event => {
        const data = event.data as string;
        if (data === "[DONE]") {
          console.info("[useDictation] SSE stream complete");
          es.close();
          eventSourceRef.current = null;
        }
      });

      es.addEventListener("error", event => {
        console.error("[useDictation] SSE error:", event);

        const isMobile = Platform.OS === "ios" || Platform.OS === "android";
        const errorMsg = isMobile
          ? "Failed to process dictation. Please check your connection and try again."
          : "Failed to process dictation. Please try again.";

        dictationStatusVar("error");
        dictationProcessingMessageVar("");
        toast.show({ action: "danger", description: errorMsg });

        es.close();
        eventSourceRef.current = null;
      });

      es.addEventListener("close", () => {
        console.info("[useDictation] SSE connection closed");
        eventSourceRef.current = null;
      });
    },
    [
      currentUser?.id,
      currentUser?.organizationId,
      fieldSchema,
      playerId,
      resolvedLocale,
      supportedLocales,
      toast,
    ]
  );

  const finishRecording = useCallback(async () => {
    const transcript = liveTranscript.trim();
    console.info("[useDictation] finishRecording called:", {
      transcriptLength: transcript.length,
      transcript,
    });

    if (!transcript) {
      console.warn("[useDictation] No transcript captured, aborting");
      dictationStatusVar("error");
      toast.show({
        action: "danger",
        description: "No speech detected. Please try again.",
      });
      await stopRecording();
      return;
    }

    await stopRecording();
    // SE-8687: read the token now, refreshing it if it has expired — a scout
    // can sit on a report long enough for the render-time one to lapse.
    const accessToken = await getFreshAccessToken(auth?.data?.accessToken);
    sendToSSE(transcript, accessToken);
  }, [liveTranscript, stopRecording, sendToSSE, toast, auth]);

  const handleCancel = useCallback(async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    await cancelAudioRecording();
  }, [cancelAudioRecording]);

  const reset = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    resetDictationState();
  }, []);

  // 2. NO SIDE EFFECTS (cleanup handled by useAudioRecorder)

  // 3. RETURN
  return {
    startRecording,
    finishRecording,
    cancelRecording: handleCancel,
    reset,
    isRecording,
    elapsedSeconds,
    liveTranscript,
    hasPermission,
    requestPermission,
    audioLevels,
  };
};
